import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { adminApi } from '@/features/admin/api/adminApi';
import { adminKeys } from '@/features/admin/api/adminQueryKeys';
export function AdminSettingsPage() {
    const queryClient = useQueryClient();
    const { data: settings, isLoading, error } = useQuery({
        queryKey: adminKeys.settings(),
        queryFn: () => adminApi.getSettings(),
    });
    const [maxParties, setMaxParties] = useState(1);
    const [maxPartyMembers, setMaxPartyMembers] = useState(20);
    const [maxPartyWorks, setMaxPartyWorks] = useState(50);
    const [feedback, setFeedback] = useState(null);
    useEffect(() => {
        if (settings) {
            setMaxParties(settings.defaultMaxParties);
            setMaxPartyMembers(settings.defaultMaxPartyMembers);
            setMaxPartyWorks(settings.defaultMaxPartyWorks);
        }
    }, [settings]);
    const mutation = useMutation({
        mutationFn: () => adminApi.updateSettings({
            defaultMaxParties: maxParties,
            defaultMaxPartyMembers: maxPartyMembers,
            defaultMaxPartyWorks: maxPartyWorks,
        }),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: adminKeys.settings() });
            setFeedback({ type: 'success', message: 'Settings saved.' });
            setTimeout(() => setFeedback(null), 3000);
        },
        onError: () => {
            setFeedback({ type: 'error', message: 'Failed to save settings.' });
            setTimeout(() => setFeedback(null), 3000);
        },
    });
    if (isLoading)
        return _jsx("div", { className: "admin-loading", children: "Loading settings..." });
    if (error)
        return _jsx("div", { className: "admin-error", children: "Failed to load settings." });
    if (!settings)
        return null;
    return (_jsxs("div", { className: "admin-dashboard", children: [_jsx("h1", { children: "Settings" }), _jsx("div", { className: "admin-stats-grid", children: _jsxs("div", { className: "admin-stat-card", style: { maxWidth: '480px' }, children: [_jsx("h3", { children: "Default User Limits" }), _jsx("p", { style: {
                                fontSize: '0.8125rem',
                                color: 'var(--color-text-muted)',
                                margin: '0 0 16px',
                                fontFamily: 'var(--font-family-body)',
                            }, children: "These defaults are applied to new users on registration." }), _jsxs("div", { style: { display: 'flex', flexDirection: 'column', gap: '14px' }, children: [_jsx(LimitField, { label: "Max Parties", value: maxParties, onChange: setMaxParties }), _jsx(LimitField, { label: "Max Party Members", value: maxPartyMembers, onChange: setMaxPartyMembers }), _jsx(LimitField, { label: "Max Party Works", value: maxPartyWorks, onChange: setMaxPartyWorks })] }), _jsxs("div", { style: { marginTop: '20px', display: 'flex', alignItems: 'center', gap: '12px' }, children: [_jsx("button", { className: "admin-btn admin-btn--active", onClick: () => mutation.mutate(), disabled: mutation.isPending, style: { padding: '8px 20px' }, children: mutation.isPending ? 'Saving...' : 'Save' }), feedback && (_jsx("span", { style: {
                                        fontSize: '0.8125rem',
                                        fontFamily: 'var(--font-family-body)',
                                        color: feedback.type === 'success' ? 'var(--color-success, #4ade80)' : 'var(--color-error, #f87171)',
                                    }, children: feedback.message }))] })] }) })] }));
}
function LimitField({ label, value, onChange, }) {
    return (_jsxs("div", { style: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px' }, children: [_jsx("label", { style: {
                    fontSize: '0.875rem',
                    fontFamily: 'var(--font-family-body)',
                    color: 'var(--color-text)',
                    fontWeight: 500,
                }, children: label }), _jsx("input", { type: "number", min: 1, value: value, onChange: (e) => onChange(Math.max(1, parseInt(e.target.value) || 1)), style: {
                    width: '80px',
                    padding: '6px 10px',
                    fontSize: '0.875rem',
                    fontFamily: 'var(--font-family-body)',
                    background: 'var(--color-surface-sunken)',
                    border: '1px solid var(--color-border)',
                    borderRadius: 'var(--radius-md)',
                    color: 'var(--color-text)',
                    textAlign: 'right',
                } })] }));
}
